import {
  ContextualData,
  JourneyEventContextualData,
  emptyJourneyEventContextualData,
} from './contextualData';
import {
  DatadogRumClientInstance,
  EventTrackingAction,
  JourneyClientInstance,
  SentryClientInstance,
  UserEvent,
} from './declarations';
import { BaseJourneyEventWrapper } from './declarations/JourneyEvent';
import { Observability, TrackingOptions } from './declarations/Observability';

// Custom instance of Observability that delegates methods to an internal instance.
// This is so we don't have to deal with the global instance being null or the
// global instance being a reassignable variable.
// It also enables people to save a reference to the global instance before they
// actually initialize it.
class ProxyObservabilityInstance<A, B, C extends BaseJourneyEventWrapper>
  implements Observability<A, B, C>
{
  currentInstance: Observability<A, B> | null = null;

  setCurrentInstance(instance: Observability<A, B>) {
    this.currentInstance = instance;
  }

  // This is only used for testing.
  _destroyCurrentInstance() {
    this.currentInstance = null;
  }

  trackEvent(
    action: EventTrackingAction<B>,
    label?: string | undefined,
    contextualData?: Partial<ContextualData> | undefined,
    options?: TrackingOptions<A> | undefined,
  ): void {
    this.currentInstance?.trackEvent(action, label, contextualData, options);
  }

  trackUserEvent(event: UserEvent, contextualData?: Partial<ContextualData> | undefined): void {
    this.currentInstance?.trackUserEvent(event, contextualData);
  }

  trackJourneyEvent(
    event: Pick<C, 'category'>,
    contextualData: JourneyEventContextualData = emptyJourneyEventContextualData,
  ): void {
    this.currentInstance?.trackJourneyEvent(event, contextualData);
  }

  trackError(
    label: string,
    error?: Error | undefined,
    contextualData?: Partial<ContextualData> | undefined,
    options?: TrackingOptions<A> | undefined,
  ): void {
    this.currentInstance?.trackError(label, error, contextualData, options);
  }

  // All of the "getXXXClient" methods return a Proxy object, that capture requests
  // and then re-evaluate and forward the request to the currentInstance every time.
  // This ensures that if the currentInstance is changed, the new instance will be used.
  // And it also allows for behavior like:
  // import { Sentry } from '@opendoor/observability';
  // If you don't do this, and Sentry is delayed in initializing, then the user will
  // have a reference to an empty object forever, instead of a reference to an object
  // that will eventually have methods in it.
  // The only downside of this is that users have to handle undefined for the methods
  // but that tradeoff is okay.

  getDatadogRumClient(): DatadogRumClientInstance {
    return new Proxy(
      {},
      {
        get: (_, prop: keyof DatadogRumClientInstance) => {
          return this.currentInstance
            ? this.currentInstance.getDatadogRumClient()[prop]
            : undefined;
        },
      },
    );
  }

  getJourneyUserClient(): JourneyClientInstance {
    return new Proxy(
      {},
      {
        get: (_, prop: keyof JourneyClientInstance) => {
          return this.currentInstance
            ? this.currentInstance.getJourneyUserClient()[prop]
            : undefined;
        },
        set: (_, prop: keyof JourneyClientInstance, value: any) => {
          return this.currentInstance
            ? (this.currentInstance.getJourneyUserClient()[prop] = value)
            : undefined;
        },
      },
    );
  }

  getJourneyEventClient(): JourneyClientInstance {
    return new Proxy(
      {},
      {
        get: (_, prop: keyof JourneyClientInstance) => {
          return this.currentInstance
            ? this.currentInstance.getJourneyEventClient()[prop]
            : undefined;
        },
        set: (_, prop: keyof JourneyClientInstance, value: any) => {
          return this.currentInstance
            ? (this.currentInstance.getJourneyEventClient()[prop] = value)
            : undefined;
        },
      },
    );
  }

  getSentryClient(): SentryClientInstance {
    return new Proxy(
      {},
      {
        get: (_, prop: keyof SentryClientInstance) =>
          this.currentInstance ? this.currentInstance.getSentryClient()[prop] : undefined,
      },
    );
  }

  isInitialized(): boolean {
    return this.currentInstance !== null;
  }
}

/**
 * Global Observability instance, set by initObservability.
 * You probably want to use observability/react/useObservability instead of this variable directly.
 * If you want to check if it is initialized, use globalObservability.isInitialized.
 */
export const globalObservability: ProxyObservabilityInstance<
  string | undefined,
  string | undefined,
  BaseJourneyEventWrapper
> = new ProxyObservabilityInstance();
